import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, ClipboardCopy, UserCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { businessDate, type HireView, type JoiningDocumentsView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Checkbox } from '../../../components/Checkbox';
import { Chip } from '../../../components/Chip';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Select } from '../../../components/Select';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { STAGE_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../lib/api-client';
import { EMPTY, formatDateTime, formatIsoDate } from '../../../lib/format';
import { useToasts } from '../../../stores/toasts';
import { usePositions } from '../../organisation/api/organisation-api';
import { HIRES_KEY, hireKey, hiresApi, useHire } from '../api/hires-api';
import { JoiningDocumentsPanel } from '../components/JoiningDocumentsPanel';
import { confirmMeta, joinedDetail } from '../joining-text';
import styles from '../components/Hires.module.css';
import { Timeline } from '../../communication/components/Timeline';

/** D13: dates and times read in Bangladesh time, with the zone shown. */
const ZONE = 'Asia/Dhaka';

/**
 * OFR-04 — F13.12 and F13.13. The accepted offer becomes an employee in a seat: the joining
 * documents first (§L11), then the joining date and the employee code (S24 Q3). The button
 * never greys out silently — what is outstanding is listed above it. An internal hire is asked
 * which seat they leave (Q8). Afterwards: time-to-slot, the onboarding summary and the whole
 * story from the hiring request to the joining. N-56 and N-57 land here.
 */
export function HirePage({ offerId }: { readonly offerId: string }): ReactNode {
  const query = useHire(offerId);
  if (query.isPending) return <HireSkeleton />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this hire" contact="Your admin" />;
    if (query.error.status === 404) return <NotFoundState />;
    if (query.error.status === 409) return <NotYetState offerId={offerId} message={query.error.message} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  return <HireScreen hire={query.data} />;
}

function HireScreen({ hire }: { readonly hire: HireView }): ReactNode {
  const queryClient = useQueryClient();
  const key = hireKey(hire.offerId);
  const writeDocuments = (documents: JoiningDocumentsView): void => {
    const current = queryClient.getQueryData<HireView>(key);
    if (current) queryClient.setQueryData<HireView>(key, { ...current, documents });
    void queryClient.invalidateQueries({ queryKey: key });
  };
  const isJoined = hire.offerStatus === 'joined';
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/offers/$offerId" params={{ offerId: hire.offerId }} className={styles.back}>
          <ArrowLeft size={14} aria-hidden />
          Offer {hire.offerReference}
        </Link>
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <div className={styles.titleLine}>
              <h1 className={styles.title}>Joining — {hire.candidateName}</h1>
              <ToneChip tone={isJoined ? STAGE_TONES.joined : STAGE_TONES.hired_pending_join}>{isJoined ? 'Joined' : 'Waiting to join'}</ToneChip>
              {hire.isInternal && (
                <Chip tone="attention" plain>
                  Internal
                </Chip>
              )}
            </div>
            <p className={styles.meta}>
              {[hire.jobTitle, hire.position ? `${hire.position.code} ${hire.position.title}` : null, hire.departmentName, hire.ventureName, hire.hiringManagerName ? `Hiring manager ${hire.hiringManagerName}` : null].filter(Boolean).join(' · ')}
            </p>
          </div>
          <UploadLinkButton hire={hire} />
        </div>
        <p className={`${styles.statusLine} ${isJoined ? styles.success : ''}`}>{statusText(hire)}</p>
        {hire.remainingCount > 0 && (
          <p className={styles.warning}>
            Every place on this job is filled. {hire.remainingCount} {hire.remainingCount === 1 ? 'person is' : 'people are'} still in the running —{' '}
            <Link to="/jobs/$jobId/close" params={{ jobId: hire.jobId }} className={styles.fileLink}>
              close them out
            </Link>
            .
          </p>
        )}
      </header>

      <div className={styles.columns}>
        <div className={styles.main}>
          {isJoined ? <EmployeeCard hire={hire} /> : <ConfirmCard hire={hire} />}
          <JoiningDocumentsPanel view={hire.documents} write={writeDocuments} />
          <Timeline subject={{ kind: 'application', id: hire.applicationId }} />
        </div>
        <div className={styles.side}>
          <PlacesCard hire={hire} />
          <Card title="30 and 90-day plans" meta={hire.plansProblem === null ? 'Written' : 'Still to write'}>
            <Link to="/hires/$applicationId/plans" params={{ applicationId: hire.applicationId }} className={styles.fileLink}>
              Open the plans
            </Link>
          </Card>
          {hire.summaryText && <SummaryCard text={hire.summaryText} />}
          <StoryCard hire={hire} />
        </div>
      </div>
    </div>
  );
}

function statusText(hire: HireView): string {
  const accepted = hire.acceptedAt ? formatDateTime(hire.acceptedAt, { timeZone: ZONE }) : EMPTY;
  if (hire.offerStatus === 'joined' && hire.employee) {
    const days = hire.employee.timeToSlotDays;
    return `Joined on ${formatIsoDate(hire.employee.joinedOn)} as ${hire.employee.employeeCode}.${days === null ? '' : ` Time to slot: ${days} ${days === 1 ? 'day' : 'days'} from accepting the offer.`}`;
  }
  return `Accepted ${accepted}. Joining ${formatIsoDate(hire.joiningDate)}.`;
}

// --- F13.13 ---------------------------------------------------------------------------------------

function ConfirmCard({ hire }: { readonly hire: HireView }): ReactNode {
  const queryClient = useQueryClient();
  const toasts = useToasts();
  const today = businessDate(new Date());
  const [joinedOn, setJoinedOn] = useState(hire.joiningDate !== null && hire.joiningDate <= today ? hire.joiningDate : today);
  const [employeeCode, setEmployeeCode] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [isLeavingSeat, setIsLeavingSeat] = useState(hire.internalSeat !== null);
  const [leavingPositionId, setLeavingPositionId] = useState<string | null>(hire.internalSeat?.id ?? null);
  const [fieldErrors, setFieldErrors] = useState<Readonly<Record<string, string>>>({});
  const [isPending, setIsPending] = useState(false);
  const positions = usePositions({ page: 1, pageSize: 200 });
  const outstanding = hire.documents.outstanding;
  // ADR 0083: the 30 and 90-day plans come before Day 1, so the button stays off, with the reason and the way to the plans.
  const isReady = outstanding.length === 0 && hire.plansProblem === null && employeeCode.trim().length >= 2 && (!isLeavingSeat || leavingPositionId !== null);

  async function confirm(): Promise<void> {
    const key = hireKey(hire.offerId);
    const before = queryClient.getQueryData<HireView>(key);
    setIsPending(true);
    setFieldErrors({});
    // Act first (CLAUDE.md §5a rule 1): it reads as joined at once, and rolls back if refused.
    if (before) queryClient.setQueryData<HireView>(key, { ...before, offerStatus: 'joined', employee: { id: '', employeeCode: employeeCode.trim().toUpperCase(), joinedOn, workEmail: workEmail.trim() || null, timeToSlotDays: null } });
    try {
      const next = await hiresApi.confirm(hire.offerId, {
        version: hire.offerVersion,
        joinedOn,
        employeeCode,
        workEmail: workEmail.trim() || null,
        leavingPositionId: hire.isInternal && isLeavingSeat ? leavingPositionId : null,
      });
      queryClient.setQueryData(key, next);
      toasts.show({ tone: 'success', message: `${hire.candidateName} has joined`, detail: joinedDetail(next) });
      void queryClient.invalidateQueries({ queryKey: HIRES_KEY });
      void queryClient.invalidateQueries({ queryKey: ['offers'] });
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
    } catch (error) {
      if (before) queryClient.setQueryData(key, before);
      if (error instanceof ApiError) setFieldErrors(error.fields);
      toasts.show({ tone: 'danger', message: 'The joining was not confirmed', detail: errorMessage(error) });
    } finally {
      setIsPending(false);
    }
  }

  return (
    <Card title="Confirm the joining" meta={confirmMeta(hire)}>
      <div className={styles.form}>
        {(outstanding.length > 0 || hire.plansProblem !== null) && (
          <div className={styles.warning}>
            <p className={styles.subhead}>Before joining can be confirmed:</p>
            <ul className={styles.missing}>
              {outstanding.map((item) => (
                <li key={item}>{item}</li>
              ))}
              {hire.plansProblem !== null && (
                <li>
                  {hire.plansProblem}{' '}
                  <Link to="/hires/$applicationId/plans" params={{ applicationId: hire.applicationId }} className={styles.fileLink}>
                    Open the plans
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}
        <div className={styles.formRow}>
          <Field label="Joined on" isRequired error={fieldErrors.joinedOn}>
            {({ inputId }) => <DatePicker id={inputId} label="Joined on" value={joinedOn} timeZone={ZONE} isInvalid={fieldErrors.joinedOn !== undefined} onChange={setJoinedOn} />}
          </Field>
          <Field label="Employee code" isRequired hint="The code from your staff system, e.g. MNG010." error={fieldErrors.employeeCode}>
            {({ inputId, describedBy }) => (
              <Input id={inputId} aria-describedby={describedBy} isMono maxLength={30} value={employeeCode} isInvalid={fieldErrors.employeeCode !== undefined} placeholder="MNG010" onChange={(event) => setEmployeeCode(event.target.value)} />
            )}
          </Field>
        </div>
        <Field label="Work email" isOptional error={fieldErrors.workEmail}>
          {({ inputId }) => <Input id={inputId} type="email" maxLength={254} value={workEmail} placeholder="name@company.com" onChange={(event) => setWorkEmail(event.target.value)} />}
        </Field>
        {hire.isInternal && (
          <div className={styles.choices}>
            <Checkbox checked={isLeavingSeat} onChange={setIsLeavingSeat} label="They leave a seat of ours — empty it on the joining day and draft a backfill" />
            {isLeavingSeat && (
              <Field label="The seat they leave" isRequired error={fieldErrors.leavingPositionId}>
                {({ inputId }) => (
                  <Select
                    id={inputId}
                    label="The seat they leave"
                    placeholder="Choose their current seat"
                    value={leavingPositionId}
                    options={(positions.data?.rows ?? [])
                      .filter((position) => position.archivedAt === null && position.id !== hire.position?.id)
                      .map((position) => ({ value: position.id, label: `${position.code} — ${position.title}`, description: `${position.departmentName}, ${position.ventureName}` }))}
                    onChange={setLeavingPositionId}
                  />
                )}
              </Field>
            )}
          </div>
        )}
        <div className={styles.actions}>
          <Button variant="solid" icon={UserCheck} disabled={!isReady || !hire.can.confirm} isPending={isPending} onClick={() => void confirm()}>
            Confirm joining
          </Button>
          {!hire.can.confirm && outstanding.length === 0 && hire.plansProblem === null && <span className={styles.quiet}>The job's recruiter or HR confirms the joining.</span>}
        </div>
      </div>
    </Card>
  );
}

function EmployeeCard({ hire }: { readonly hire: HireView }): ReactNode {
  const employee = hire.employee;
  if (!employee) return null;
  return (
    <Card title="Employee record" meta="Handed to the staff system as employee.created">
      <dl className={styles.facts}>
        <dt>Employee code</dt>
        <dd className={styles.figure}>{employee.employeeCode}</dd>
        <dt>Joined on</dt>
        <dd>{formatIsoDate(employee.joinedOn)}</dd>
        <dt>Time to slot</dt>
        <dd>{employee.timeToSlotDays === null ? EMPTY : `${employee.timeToSlotDays} ${employee.timeToSlotDays === 1 ? 'day' : 'days'}`}</dd>
        <dt>Seat</dt>
        <dd>{hire.position ? `${hire.position.code} — ${hire.position.title}` : EMPTY}</dd>
        <dt>Work email</dt>
        <dd>{employee.workEmail ?? EMPTY}</dd>
      </dl>
    </Card>
  );
}

// --- The side ------------------------------------------------------------------------------------------

function PlacesCard({ hire }: { readonly hire: HireView }): ReactNode {
  return (
    <Card title="Places on this job" meta={hire.jobStatus === 'filled' ? 'Filled' : undefined}>
      <dl className={styles.facts}>
        <dt>Places</dt>
        <dd>{hire.places.total}</dd>
        <dt>Joined</dt>
        <dd>{hire.places.filled}</dd>
        <dt>Waiting to join</dt>
        <dd>{hire.places.waitingToJoin}</dd>
      </dl>
    </Card>
  );
}

/** E11 "handoff summary for onboarding". */
function SummaryCard({ text }: { readonly text: string }): ReactNode {
  const toasts = useToasts();
  return (
    <Card
      title="Joining summary"
      meta="For whoever does onboarding"
      action={
        <Button
          variant="outline"
          size="sm"
          icon={ClipboardCopy}
          onClick={() =>
            void navigator.clipboard
              .writeText(text)
              .then(() => toasts.show({ tone: 'success', message: 'Summary copied' }))
              .catch((error: unknown) => toasts.show({ tone: 'danger', message: "It wasn't copied", detail: errorMessage(error) }))
          }
        >
          Copy
        </Button>
      }
    >
      <p className={styles.summary}>{text}</p>
    </Card>
  );
}

function StoryCard({ hire }: { readonly hire: HireView }): ReactNode {
  return (
    <Card title="The whole story" meta="From the hiring request to the joining">
      <ol className={styles.timeline}>
        {[...hire.story].reverse().map((entry, index) => (
          <li key={`${entry.at}-${index}`} className={styles.timelineItem}>
            <span className={styles.timelineLabel}>{entry.label}</span>
            <span className={styles.meta}>
              {entry.actorName ?? 'The system'} · {formatDateTime(entry.at, { timeZone: ZONE })}
            </span>
          </li>
        ))}
      </ol>
    </Card>
  );
}

/** S24 Q1: an answer taken by phone means the candidate never saw the page — send them its link. */
function UploadLinkButton({ hire }: { readonly hire: HireView }): ReactNode {
  const toasts = useToasts();
  const [isPending, setIsPending] = useState(false);
  if (!hire.can.copyUploadLink) return null;
  return (
    <Button
      variant="outline"
      icon={ClipboardCopy}
      isPending={isPending}
      onClick={() => {
        setIsPending(true);
        hiresApi
          .uploadLink(hire.offerId)
          .then(async ({ text }) => {
            await navigator.clipboard.writeText(text);
            toasts.show({ tone: 'success', message: 'Documents link copied', detail: 'Paste it into WhatsApp.' });
          })
          .catch((error: unknown) => toasts.show({ tone: 'danger', message: "The link wasn't copied", detail: errorMessage(error) }))
          .finally(() => setIsPending(false));
      }}
    >
      Copy documents link
    </Button>
  );
}

function NotYetState({ offerId, message }: { readonly offerId: string; readonly message: string }): ReactNode {
  return (
    <div className={styles.page}>
      <p className={styles.statusLine}>{message}</p>
      <Link to="/offers/$offerId" params={{ offerId }} className={styles.fileLink}>
        Open the offer
      </Link>
    </div>
  );
}

function HireSkeleton(): ReactNode {
  return (
    <div className={styles.page}>
      <Skeleton shape="title" width="40%" />
      <SkeletonLines count={2} />
      <SkeletonLines count={8} />
    </div>
  );
}
